import { Component, Input, OnInit } from '@angular/core';
import { ArgyleIncomeStream } from 'src/app/models/Argyle';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/services/auth.service';
import { ApplicantDataService } from 'src/app/services/applicant-data.service';
import { GroupApplicantsService } from 'src/app/services/group-applicants.service';

interface PaystubRow {
  raw: any;
  periodStart: Date | null;
  periodEnd: Date | null;
  paystubDate: Date | null;
  periodDays: number | null;
  grossPay: number;
  netPay: number;
  netPct: number | null;
  gapDaysBefore: number | null;
  varianceFromMeanPct: number | null;
  isLowOutlier: boolean;
}

@Component({
  selector: 'app-payroll-sync',
  templateUrl: './payroll-sync.component.html',
  styleUrls: ['./payroll-sync.component.less']
})
export class PayrollSyncComponent implements OnInit {
  @Input() stream!: ArgyleIncomeStream;

  constructor(
    public modalService: NgbModal,
    private toast: ToastrService,
    private auth: AuthService,
    public appData: ApplicantDataService,
    public groups: GroupApplicantsService
  ) { }

  ngOnInit(): void { }

  // ---------- Pay-cycle helpers ----------
  public payPeriodsPerYear(payCycle: string): number | null {
    if (!payCycle) { return null; }
    const map: { [k: string]: number } = {
      weekly: 52,
      biweekly: 26,
      'bi-weekly': 26,
      semimonthly: 24,
      'semi-monthly': 24,
      monthly: 12,
      quarterly: 4,
      annually: 1,
      yearly: 1,
      daily: 260
    };
    return map[payCycle.toLowerCase()] || null;
  }

  public expectedPeriodDays(payCycle: string): number | null {
    if (!payCycle) { return null; }
    const map: { [k: string]: number } = {
      weekly: 7,
      biweekly: 14,
      'bi-weekly': 14,
      semimonthly: 15,
      'semi-monthly': 15,
      monthly: 30
    };
    return map[payCycle.toLowerCase()] || null;
  }

  public expectedMonthlyFromBase(record: any): number | null {
    const amt = parseFloat(record?.identityData?.base_pay?.amount);
    const period = (record?.identityData?.base_pay?.period || '').toLowerCase();
    const cycle = record?.identityData?.pay_cycle;
    if (isNaN(amt)) { return null; }
    if (period === 'hourly') {
      // assume 40 hr/wk * 52wk = 2080 hr/yr, fallback if not provided
      return (amt * 2080) / 12;
    }
    if (period === 'yearly' || period === 'annually') { return amt / 12; }
    if (period === 'monthly') { return amt; }
    const periods = this.payPeriodsPerYear(cycle);
    if (periods) { return (amt * periods) / 12; }
    return null;
  }

  // ---------- Tenure ----------
  public tenureMonths(hireDate: string): number | null {
    if (!hireDate) { return null; }
    const start = new Date(hireDate);
    if (isNaN(start.getTime())) { return null; }
    const now = new Date();
    let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    if (now.getDate() < start.getDate()) { months -= 1; }
    return months < 0 ? 0 : months;
  }

  public formatTenure(hireDate: string): string {
    const m = this.tenureMonths(hireDate);
    if (m == null) { return '—'; }
    if (m < 12) { return `${m} mo`; }
    const yrs = Math.floor(m / 12);
    const rem = m % 12;
    return rem === 0 ? `${yrs} yr` : `${yrs} yr ${rem} mo`;
  }

  // ---------- Identity match against the invite ----------
  public identityMismatches(record: any): string[] {
    const issues: string[] = [];
    const id = record?.identityData;
    if (!id) { return issues; }
    const eqI = (a: string, b: string) =>
      (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

    if (this.stream?.firstName && id.first_name && !eqI(this.stream.firstName, id.first_name)) {
      issues.push(`First name: invite "${this.stream.firstName}" vs payroll "${id.first_name}"`);
    }
    if (this.stream?.lastName && id.last_name && !eqI(this.stream.lastName, id.last_name)) {
      issues.push(`Last name: invite "${this.stream.lastName}" vs payroll "${id.last_name}"`);
    }
    return issues;
  }

  public maskSsn(ssn: string): string {
    if (!ssn) { return '—'; }
    const digits = ssn.replace(/\D/g, '');
    if (digits.length < 4) { return '***'; }
    return `***-**-${digits.slice(-4)}`;
  }

  // ---------- Paystub computation ----------
  public buildPaystubRows(record: any): PaystubRow[] {
    const stubs = record?.paystubData || [];
    if (!stubs.length) { return []; }

    const sorted = [...stubs].sort((a, b) => {
      const ad = a.paystubDate ? new Date(a.paystubDate).getTime() : 0;
      const bd = b.paystubDate ? new Date(b.paystubDate).getTime() : 0;
      return bd - ad;
    });

    const grosses = sorted.map(s => Number(s.grossPay) || 0);
    const meanGross = grosses.reduce((a, b) => a + b, 0) / (grosses.length || 1);
    const expectedDays = this.expectedPeriodDays(record?.identityData?.pay_cycle);

    // Walk oldest → newest to compute gap-since-previous, then re-sort newest first.
    const oldestFirst = [...sorted].reverse();
    const rows: PaystubRow[] = oldestFirst.map((s, idx) => {
      const periodStart = s.periodStartDate ? new Date(s.periodStartDate) : null;
      const periodEnd = s.periodEndDate ? new Date(s.periodEndDate) : null;
      const paystubDate = s.paystubDate ? new Date(s.paystubDate) : null;
      const periodDays = periodStart && periodEnd
        ? Math.round((periodEnd.getTime() - periodStart.getTime()) / 86400000) + 1
        : null;
      const gross = Number(s.grossPay) || 0;
      const net = Number(s.netPay) || 0;
      const netPct = gross > 0 ? (net / gross) * 100 : null;

      let gapDaysBefore: number | null = null;
      if (idx > 0 && expectedDays) {
        const prevEnd = oldestFirst[idx - 1].periodEndDate
          ? new Date(oldestFirst[idx - 1].periodEndDate)
          : null;
        if (prevEnd && periodStart) {
          const diff = Math.round((periodStart.getTime() - prevEnd.getTime()) / 86400000);
          // diff of 1 day is normal (next day after prev period); flag larger
          if (diff > expectedDays / 2) { gapDaysBefore = diff; }
        }
      }

      const variance = meanGross > 0 ? ((gross - meanGross) / meanGross) * 100 : null;

      return {
        raw: s,
        periodStart,
        periodEnd,
        paystubDate,
        periodDays,
        grossPay: gross,
        netPay: net,
        netPct,
        gapDaysBefore,
        varianceFromMeanPct: variance,
        isLowOutlier: variance != null && variance < -25
      };
    });

    return rows.reverse(); // newest first for display
  }

  public totalsFor(rows: PaystubRow[]): {
    count: number;
    grossSum: number;
    netSum: number;
    grossAvg: number;
    netAvg: number;
    coverageDays: number;
    coverageWeeks: number;
  } {
    const count = rows.length;
    const grossSum = rows.reduce((a, r) => a + r.grossPay, 0);
    const netSum = rows.reduce((a, r) => a + r.netPay, 0);
    const dates = rows
      .flatMap(r => [r.periodStart, r.periodEnd])
      .filter(d => d) as Date[];
    let coverageDays = 0;
    if (dates.length) {
      const min = Math.min(...dates.map(d => d.getTime()));
      const max = Math.max(...dates.map(d => d.getTime()));
      coverageDays = Math.round((max - min) / 86400000) + 1;
    }
    return {
      count,
      grossSum,
      netSum,
      grossAvg: count ? grossSum / count : 0,
      netAvg: count ? netSum / count : 0,
      coverageDays,
      coverageWeeks: Math.round((coverageDays / 7) * 10) / 10
    };
  }

  public statusClass(record: any): string {
    const s = (record?.identityData?.employment_status || this.stream?.status || '').toLowerCase();
    if (s === 'active') { return 'status-active'; }
    if (s === 'terminated' || s === 'inactive') { return 'status-inactive'; }
    return 'status-other';
  }

  public get failedSopChecks() {
    return this.stream?.sopChecks?.filter(c => !c.passed) ?? [];
  }

  public hasFailedCheck(name: string): boolean {
    return this.stream?.sopChecks?.some(c => c.checkName === name && !c.passed) ?? false;
  }

  public getCheckReason(name: string): string | null {
    return this.stream?.sopChecks?.find(c => c.checkName === name && !c.passed)?.reason ?? null;
  }
}
